import type {
  BuildingMaterialType,
  BuildingTypeEnum,
  Filter,
  GarageLocalizationType,
  MarketType,
  NeighborhoodType,
  OfferCategory,
  OfferType,
  PlotTypeEnum,
  PropertyConditionType,
  SellerType,
  ServiceUserTypeEnum,
} from "@prisma/client";
import type { FiltersState } from "./OffersSlice";

export type PaginationState = {
  page?: number;
  perPage?: number;
  sort?: string;
};

export function getFilterUrlParams(
  filters: FiltersState,
  pagination: PaginationState,
) {
  const params = new URLSearchParams();
  if (filters.type) {
    params.set("type", filters.type);
  }
  if (filters.priceFrom) {
    params.set("priceFrom", filters.priceFrom.toString());
  }
  if (filters.priceTo) {
    params.set("priceTo", filters.priceTo.toString());
  }
  if (pagination.sort) {
    params.set("sort", pagination.sort);
  }
  if (filters.areaFrom) {
    params.set("areaFrom", filters.areaFrom.toString());
  }
  if (filters.areaTo) {
    params.set("areaTo", filters.areaTo.toString());
  }
  if (pagination.page) {
    params.set("page", pagination.page.toString());
  }
  if (pagination.perPage) {
    params.set("perPage", pagination.perPage.toString());
  }
  if (filters.rooms) {
    for (const room of filters.rooms) {
      params.append("rooms", room.toString());
    }
  }
  if (filters.floors) {
    for (const floor of filters.floors) {
      params.append("floors", floor.toString());
    }
  }
  if (filters.buildType) {
    for (const buildingType of filters.buildType) {
      params.append("buildType", buildingType);
    }
  }
  if (filters.selType) {
    params.set("selType", filters.selType);
  }
  if (filters.markType) {
    params.set("markType", filters.markType);
  }
  if (filters.priceMFrom) {
    params.set("priceMFrom", filters.priceMFrom.toString());
  }
  if (filters.priceMTo) {
    params.set("priceMTo", filters.priceMTo.toString());
  }
  if (filters.numFloorsFrom) {
    params.set("numFloorsFrom", filters.numFloorsFrom.toString());
  }
  if (filters.numFloorsTo) {
    params.set("numFloorsTo", filters.numFloorsTo.toString());
  }
  if (filters.buildYearFrom) {
    params.set("buildYearFrom", filters.buildYearFrom.toString());
  }
  if (filters.buildYearTo) {
    params.set("buildYearTo", filters.buildYearTo.toString());
  }
  if (filters.materialType) {
    for (const buildingMaterialType of filters.materialType) {
      params.append("materialType", buildingMaterialType);
    }
  }
  if (filters.search) {
    params.set("search", filters.search);
  }
  if (filters.placeId) {
    for (const townId of filters.placeId) {
      params.append("placeId", townId.toString());
    }
  }
  if (filters.placeLvl) {
    for (const lvl of filters.placeLvl) {
      params.append("placeLvl", lvl);
    }
  }
  if (filters.placeDist) {
    params.set("placeDist", filters.placeDist.toString());
  }
  if (filters.plotFrom) {
    params.set("plotFrom", filters.plotFrom.toString());
  }
  if (filters.plotTo) {
    params.set("plotTo", filters.plotTo.toString());
  }
  if (filters.plotType) {
    for (const plotType of filters.plotType) {
      params.append("plotType", plotType);
    }
  }
  if (filters.neighType) {
    for (const neighType of filters.neighType) {
      params.append("neighType", neighType);
    }
  }
  if (filters.minPlotSide) {
    params.set("minPlotSide", filters.minPlotSide.toString());
  }
  if (filters.maxPlotSide) {
    params.set("maxPlotSide", filters.maxPlotSide.toString());
  }
  if (filters.garLoc) {
    for (const garLoc of filters.garLoc) {
      params.append("garLoc", garLoc);
    }
  }
  if (filters.useType) {
    for (const useType of filters.useType) {
      params.append("useType", useType);
    }
  }
  if (filters.filter) {
    params.set("filter", filters.filter.toString());
  }
  if (filters.bounds && filters.bounds.length === 4) {
    params.set("bounds", filters.bounds.join(","));
  }
  if (filters.zoom) {
    params.set("zoom", filters.zoom.toString());
  }
  if (filters.view) {
    params.set("view", filters.view);
  }
  if (filters.discountTo) {
    params.set("discountTo", filters.discountTo.toString());
  }
  if (filters.conds) {
    for (const cond of filters.conds) {
      params.append("conds", cond);
    }
  }
  if (filters.category) {
    for (const category of filters.category) {
      params.append("category", category);
    }
  }
  return params.toString();
}

export function createPaginationStateFromUrlParams(
  searchParams: URLSearchParams,
): PaginationState {
  const state: PaginationState = {};
  const sort = searchParams.has("sort") && (searchParams.get("sort") as string);
  if (sort) {
    state.sort = sort;
  }
  const page =
    searchParams.has("page") &&
    Number.parseInt(searchParams.get("page") as string);
  if (page) {
    state.page = page;
  }
  const perPage =
    searchParams.has("perPage") &&
    Number.parseInt(searchParams.get("perPage") as string);
  if (perPage) {
    state.perPage = perPage;
  }
  return state;
}

export function createFiltersStateFromUrlParams(
  searchParams: URLSearchParams,
): FiltersState {
  const result: FiltersState = {};

  if (searchParams.has("filter")) {
    result.filter = Number.parseInt(searchParams.get("filter") as string);
  }
  if (searchParams.has("type")) {
    result.type = searchParams.get("type") as OfferType;
  }
  if (searchParams.has("priceFrom")) {
    result.priceFrom = Number.parseFloat(
      searchParams.get("priceFrom") as string,
    );
  }
  if (searchParams.has("priceTo")) {
    result.priceTo = Number.parseFloat(searchParams.get("priceTo") as string);
  }
  if (searchParams.has("areaFrom")) {
    result.areaFrom = Number.parseFloat(searchParams.get("areaFrom") as string);
  }
  if (searchParams.has("areaTo")) {
    result.areaTo = Number.parseFloat(searchParams.get("areaTo") as string);
  }
  const rooms = searchParams
    .getAll("rooms")
    .map((room) => Number.parseInt(room));
  if (rooms.length > 0) {
    result.rooms = rooms;
  }
  const floors = searchParams.getAll("floors").map((f) => Number.parseInt(f));
  if (floors.length > 0) {
    result.floors = floors;
  }
  const buildType = searchParams.getAll("buildType") as BuildingTypeEnum[];
  if (buildType.length > 0) {
    result.buildType = buildType;
  }
  if (searchParams.has("selType")) {
    result.selType = searchParams.get("selType") as SellerType;
  }
  if (searchParams.has("markType")) {
    result.markType = searchParams.get("markType") as MarketType;
  }
  if (searchParams.has("priceMFrom")) {
    result.priceMFrom = Number.parseFloat(
      searchParams.get("priceMFrom") as string,
    );
  }
  if (searchParams.has("priceMTo")) {
    result.priceMTo = Number.parseFloat(searchParams.get("priceMTo") as string);
  }
  if (searchParams.has("numFloorsFrom")) {
    result.numFloorsFrom = Number.parseInt(
      searchParams.get("numFloorsFrom") as string,
    );
  }
  if (searchParams.has("numFloorsTo")) {
    result.numFloorsTo = Number.parseInt(
      searchParams.get("numFloorsTo") as string,
    );
  }
  if (searchParams.has("buildYearFrom")) {
    result.buildYearFrom = Number.parseInt(
      searchParams.get("buildYearFrom") as string,
    );
  }
  if (searchParams.has("buildYearTo")) {
    result.buildYearTo = Number.parseInt(
      searchParams.get("buildYearTo") as string,
    );
  }
  const materialType = searchParams.getAll(
    "materialType",
  ) as BuildingMaterialType[];
  if (materialType.length > 0) {
    result.materialType = materialType;
  }
  if (searchParams.has("search")) {
    result.search = searchParams.get("search") as string;
  }
  if (searchParams.has("placeDist")) {
    result.placeDist = Number.parseInt(searchParams.get("placeDist") as string);
  }
  const placeId = searchParams
    .getAll("placeId")
    .map((id) => Number.parseInt(id));
  if (placeId.length > 0) {
    result.placeId = placeId;
  }
  const placeLvl = searchParams.getAll("placeLvl");
  if (placeLvl.length > 0) {
    result.placeLvl = placeLvl;
  }
  if (searchParams.has("plotFrom")) {
    result.plotFrom = Number.parseFloat(searchParams.get("plotFrom") as string);
  }
  if (searchParams.has("plotTo")) {
    result.plotTo = Number.parseFloat(searchParams.get("plotTo") as string);
  }
  const plotType = searchParams.getAll("plotType") as PlotTypeEnum[];
  if (plotType.length > 0) {
    result.plotType = plotType;
  }
  const neighType = searchParams.getAll("neighType") as NeighborhoodType[];
  if (neighType.length > 0) {
    result.neighType = neighType;
  }
  if (searchParams.has("minPlotSide")) {
    result.minPlotSide = Number.parseFloat(
      searchParams.get("minPlotSide") as string,
    );
  }
  if (searchParams.has("maxPlotSide")) {
    result.maxPlotSide = Number.parseFloat(
      searchParams.get("maxPlotSide") as string,
    );
  }
  const garLoc = searchParams.getAll("garLoc") as GarageLocalizationType[];
  if (garLoc.length > 0) {
    result.garLoc = garLoc;
  }
  const useType = searchParams.getAll("useType") as ServiceUserTypeEnum[];
  if (useType.length > 0) {
    result.useType = useType;
  }
  if (searchParams.has("bounds")) {
    const bounds = searchParams
      .get("bounds")
      ?.split(",")
      .map(Number.parseFloat);
    if (bounds && bounds.length === 4) {
      result.bounds = bounds;
    }
  }
  if (searchParams.has("isGreatOffer")) {
    result.isGreatOffer = true;
  }
  if (searchParams.has("zoom")) {
    result.zoom = Number.parseInt(searchParams.get("zoom") as string);
  }
  if (searchParams.has("view")) {
    result.view = searchParams.get("view") as string;
  }
  if (searchParams.has("discountTo")) {
    result.discountTo = Number.parseFloat(
      searchParams.get("discountTo") as string,
    );
  }
  const conds = searchParams.getAll("conds") as PropertyConditionType[];
  if (conds.length > 0) {
    result.conds = conds;
  }

  const category = searchParams.getAll("category") as OfferCategory[];
  if (category.length > 0) {
    result.category = category;
  }
  return result;
}

export function createFiltersStateFromFilter(filter: Filter): FiltersState {
  const placeId: number[] = [];
  const placeLvl: string[] = [];
  for (const townId of filter.townIds || []) {
    placeId.push(townId);
    placeLvl.push("town");
  }
  for (const communeId of filter.communeIds || []) {
    placeId.push(communeId);
    placeLvl.push("commune");
  }
  for (const countyId of filter.countyIds || []) {
    placeId.push(countyId);
    placeLvl.push("county");
  }
  for (const voivodeshipId of filter.voivodeshipIds || []) {
    placeId.push(voivodeshipId);
    placeLvl.push("voivodeship");
  }
  return {
    filter: filter.id,
    type: filter.type || undefined,
    priceFrom: filter.priceFrom || undefined,
    priceTo: filter.priceTo || undefined,
    areaFrom: filter.areaFrom || undefined,
    areaTo: filter.areaTo || undefined,
    rooms: filter.rooms || undefined,
    floors: filter.floors || undefined,
    buildType: filter.buildTypes || undefined,
    selType: filter.sellerType || undefined,
    markType: filter.marketType || undefined,
    priceMFrom: filter.priceMFrom || undefined,
    priceMTo: filter.priceMTo || undefined,
    numFloorsFrom: filter.numFloorsFrom || undefined,
    numFloorsTo: filter.numFloorsTo || undefined,
    buildYearFrom: filter.buildYearFrom || undefined,
    buildYearTo: filter.buildYearTo || undefined,
    materialType: filter.materialTypes || undefined,
    search: filter.search || undefined,
    placeId,
    placeLvl,
    plotFrom: filter.plotFrom || undefined,
    plotTo: filter.plotTo || undefined,
    plotType: filter.plotTypes || undefined,
    neighType: filter.neighTypes || undefined,
    minPlotSide: filter.minPlotSide || undefined,
    maxPlotSide: filter.maxPlotSide || undefined,
    garLoc: filter.garageLocalizations || undefined,
    useType: filter.useTypes || undefined,
    isGreatOffer: filter.isGreatOffer || undefined,
    bounds: filter.bounds || undefined,
    discountTo: filter.discountTo || undefined,
    category: filter.categories || undefined,
  };
}
